import { newLogger } from "@/utils/util";
import network from "@/utils/network";
import Vue from "vue";
import {
  LEASING_PRELIM_FIXED_STEPS,
  LEASING_PRELIM_PSD2_FIXED_STEPS,
  PRELIM_TYPE,
} from "@/utils/const";

let logger = newLogger("LeasingPrescoreStore");

const urls = {
  questions: (type) => `/administration/leasing-prescore/questions/${type}`,
  assessment: "/credit/leasing-prescore",
  latestAssessment: "/credit/my-leasing-prescore",
  getPrescoreByPublicId: (publicId) =>
    `/credit/leasing-prescore-public-id/${publicId}`,
  patchPrescore: (publicId) => `/credit/leasing-prescore/update/${publicId}`,
};

const conditions = {
  company_registered_abroad: { field: "source_of_income", answer: "employee" },
  current_job: { field: "source_of_income", answer: "employee" },
  trial_period: { field: "source_of_income", answer: "employee" },
  notice_period: { field: "source_of_income", answer: "employee" },
  incapacity: { field: "source_of_income", answer: "employee" },
  continuous_employment: { field: "source_of_income", answer: "employee" },
  fixed_term_employment_contract: {
    field: "source_of_income",
    answer: "employee",
  },
  fixed_term_employment_contract_length: {
    field: "fixed_term_employment_contract",
    answer: "true",
  },
  scope_of_activities: { field: "source_of_income", answer: "self_employed" },
  closed_business_years: { field: "source_of_income", answer: "self_employed" },
  disability_pension: { field: "source_of_income", answer: "pensioner" },
  committed_salary: { field: "salary_commitment", answer: "true" },
};

/**
 * Administration PreliminaryConfig store
 */

const initState = () => {
  return {
    currentStepIndex: 0,
    steps: [],
    questions: [],
    data: {},
    prescore: {},
  };
};

const actions = {
  fetchSteps: async ({ commit }, type) => {
    try {
      let axios = await network.connection();
      let { data } = await axios.get(urls.questions(type));
      commit("save", data);
    } catch (err) {
      logger.error(err);
      await Promise.reject(err);
    }
  },
  fetchPrescoreByPublicId: async ({ commit, dispatch }, publicId) => {
    try {
      let axios = await network.connection();
      let { data } = await axios.get(urls.getPrescoreByPublicId(publicId));
      commit("savePrescore", data);
      await dispatch("fetchSteps", data.type);
      commit("setType", data.type);
      commit("setStepIndex", data.step);
      if (data && data.dynamicData) {
        data.dynamicData = JSON.parse(data.dynamicData);

        Object.keys(data.dynamicData).forEach((key) => {
          commit("saveData", {
            question: key,
            answer: data.dynamicData[key],
          });
        });
      }
    } catch (err) {
      logger.error(err);
      await Promise.reject(err);
    }
  },
  updatePrescore: async ({ commit, getters, rootGetters }, additionalData) => {
    try {
      const publicId = rootGetters["leasing/publicId"];
      let axios = await network.connection();
      let { data } = await axios.patch(urls.patchPrescore(publicId), {
        ...additionalData,
        dynamicData: JSON.stringify(getters["getData"]),
      });
      commit("savePrescore", data);
    } catch (err) {
      logger.error(err);
      await Promise.reject(err);
    }
  },
  addData: ({ commit }, data) => {
    try {
      commit("saveData", data);
    } catch (err) {
      logger.error(err);
    }
  },
  submit: async ({ commit, state, rootGetters }) => {
    try {
      commit("offers/unload", null, { root: true });
      let axios = await network.connection();
      let request = {
        publicId: rootGetters["leasing/publicId"],
        amount: state.data.amount,
        duration: state.data.duration,
        downPayment: state.data.downPayment,
        extraAmount: state.data.extraAmount,
        salary: state.data.income,
        salaryCommitment: state.data.salary_commitment,
        committedSalary: state.data.committed_salary,
        type: state.data[PRELIM_TYPE.KEY],
        dynamicData: JSON.stringify(state.data),
      };
      let { data } = await axios.post(urls.assessment, request, {
        timeout: 120000,
      });
      commit("offers/saveOffers", data, { root: true });
      return { success: data.isEligible, redirectUrl: data.redirectUrl };
    } catch (err) {
      logger.error(err);
      await Promise.reject(err);
    }
  },
  fetchLatest: async ({ commit }) => {
    try {
      let axios = await network.connection();
      let { data } = await axios.get(urls.latestAssessment);

      if (data && data.dynamicData) {
        data.dynamicData = JSON.parse(data.dynamicData);

        Object.keys(data.dynamicData).forEach((key) => {
          commit("saveData", {
            question: key,
            answer: data.dynamicData[key],
          });
        });
      }
    } catch (err) {
      logger.error(err);
      await Promise.reject(err);
    }
  },
  recalculate: async (
    { commit, state, rootGetters },
    { amount, duration, downPayment, extraAmount }
  ) => {
    commit("offers/unload", null, { root: true });
    let axios = await network.connection();
    let request = {
      publicId: rootGetters["leasing/publicId"],
      amount,
      duration,
      downPayment,
      extraAmount,
      salary: state.data.income,
      salaryCommitment: state.data.salary_commitment,
      committedSalary: state.data.committed_salary,
      dynamicData: JSON.stringify(state.data),
    };

    let { data } = await axios.post(urls.assessment, request);
    commit("offers/saveOffers", data, { root: true });
    return data.isEligible;
  },
  // eslint-disable-next-line unused-imports/no-unused-vars
  drop: async ({ state }) => {
    state = initState();
  },
  backToFirstStep: async ({ commit }) => {
    commit("backToFirstStep");
  },
};

const mutations = {
  save: (state, data) => {
    state.steps = [];
    state.questions = [];
    let t = 0;
    for (let s in data.steps) {
      state.steps.push(data.steps[s]);
      t += data.steps[s].time;
      for (let q in data.steps[s].questions) {
        state.questions.push(data.steps[s].questions[q]);
      }
    }
    Vue.set(state, "time", t);
  },
  saveData: (state, { question, answer }) => {
    Vue.set(state.data, question, answer);
  },
  nextStep: (state) => {
    state.currentStepIndex++;
  },
  previousStep: (state) => {
    state.currentStepIndex--;
  },
  backToFirstStep: (state) => {
    state.currentStepIndex = 0;
  },
  setStepIndex: (state, index) => {
    console.log("setStepIndex", index);
    if (index < 0) {
      index = 0;
    }
    if (
      index >
      state.steps.length + Object.keys(LEASING_PRELIM_FIXED_STEPS).length
    ) {
      index =
        state.steps.length + Object.keys(LEASING_PRELIM_FIXED_STEPS).length;
    }
    state.currentStepIndex = index;
  },
  setType: (state, type) => {
    Vue.set(state.data, PRELIM_TYPE.KEY, type);
  },
  savePrescore: (state, data) => {
    Vue.set(state, "prescore", data);
  },
};

const getters = {
  currentStepIndex(state) {
    return state.currentStepIndex;
  },
  isPsd2(state) {
    return state.data[PRELIM_TYPE.KEY] === PRELIM_TYPE.PSD2;
  },
  getType(state) {
    return state.data[PRELIM_TYPE.KEY];
  },
  fixedSteps(state, getters) {
    let steps = Object.keys(LEASING_PRELIM_FIXED_STEPS).length;
    if (getters.isPsd2) {
      steps += Object.keys(LEASING_PRELIM_PSD2_FIXED_STEPS).length;
    }
    return steps;
  },
  steps(state) {
    return state.steps;
  },
  step: (state, getters) => (id) => {
    if (!id || id < getters.fixedSteps) {
      return null;
    } else {
      return state.steps[parseInt(id) - getters.fixedSteps];
    }
  },
  maxTime: (state, getters) => {
    return Math.round((state.time ? state.time : 0) / 60) + getters.fixedSteps;
  },
  stepTime: (state, getters) => (id) => {
    if (!id || id < getters.fixedSteps) return getters.fixedSteps - id;
    return state.steps[parseInt(id) - getters.fixedSteps].time / 60;
  },
  answer: (state) => (question) => {
    return state.data[question];
  },
  checkStep: (state, getters) => (id) => {
    if (!id || id === LEASING_PRELIM_FIXED_STEPS.AMOUNT_AND_TERM) {
      return true;
    }
    if (id === LEASING_PRELIM_FIXED_STEPS.TYPE_SELECT) {
      return !!state.data[PRELIM_TYPE.KEY];
    }

    if (getters.isPsd2 && id === LEASING_PRELIM_PSD2_FIXED_STEPS.SYNC) {
      return true;
    }

    id = parseInt(id) - getters.fixedSteps;
    let identifiers = [];
    for (let q in state.steps[id].questions) {
      let identifier = state.steps[id].questions[q].identifier;
      if (
        !Object.keys(conditions).includes(identifier) ||
        state.data[conditions[identifier].field] ===
          conditions[identifier].answer
      ) {
        let question = state.steps[id].questions[q];
        if (question.type === "DATE_PICKER") {
          let value = state.data[identifier];
          if (value && question.validation) {
            let today = new Date();
            let dateValue = new Date(value);
            today.setHours(0, 0, 0, 0);
            dateValue.setHours(0, 0, 0, 0);
            if (question.validation === "PAST" && today < dateValue) {
              return false;
            } else if (question.validation === "FUTURE" && today > dateValue) {
              return false;
            }
          }
        }
        if (question.type === "INPUT") {
          // all prelim input fields are number only
          if (isNaN(state.data[identifier])) {
            // NaN check
            return false;
          }
        }
        identifiers.push(identifier);
      }
    }
    let ans = true;
    for (let i in identifiers) {
      ans = ans && !!state.data[identifiers[i]];
    }
    return ans;
  },
  isVisible: (state) => (identifier) => {
    if (!Object.keys(conditions).includes(identifier)) return true;
    return (
      state.data[conditions[identifier].field] === conditions[identifier].answer
    );
  },
  getPrescore: (state) => {
    return state.prescore;
  },
  getData: (state) => {
    return state.data;
  },
};

export default {
  namespaced: true,
  state: initState(),
  mutations: mutations,
  actions: actions,
  getters: getters,
};
